"use client";

import SectionTitle from "@/shared/app/ui/text/PageSectionTitle";
import { useState } from "react";

interface InquiryForm {
  company: string;
  name: string;
  phone: string;
  email: string;
  projectType: string;
  budget: string;
  content: string;
}

const projectTypes = [
  "웹사이트 제작",
  "모바일 앱 개발",
  "플랫폼 구축",
  "솔루션 개발",
  "유지보수/운영",
  "기타",
];

const budgetRanges = [
  "1,000만원 미만",
  "1,000만원 ~ 3,000만원",
  "3,000만원 ~ 5,000만원",
  "5,000만원 ~ 1억원",
  "1억원 이상",
  "협의 필요",
];

export default function InquiryFormSection() {
  const [form, setForm] = useState<InquiryForm>({
    company: "",
    name: "",
    phone: "",
    email: "",
    projectType: "",
    budget: "",
    content: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch("/api/inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!data.ok) {
        setError(data.error || "문의 전송에 실패했습니다.");
        return;
      }

      setSubmitted(true);
    } catch {
      setError("네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 md:px-5 md:py-4 border border-[#C8C8C8]/40 rounded-2xl text-sm md:text-base focus:outline-none focus:border-[#347E82] focus:ring-1 focus:ring-[#347E82] transition-colors bg-white shadow-sm";
  const labelClass =
    "block text-sm md:text-base text-midGray-50 mb-2 tracking-tight";

  return (
    <div className="border-t border-[#ECECEC] mt-[-2px]">
      <div className="w-full xl:max-w-[1280px] mx-auto px-5 xl:px-2 py-14 md:py-20">
        <SectionTitle
          align="left"
          label="Inquiry"
          title={
            <>
              프로젝트를 함께
              <br />
              만들어갑니다
            </>
          }
          description={
            <>
              아래 양식을 작성해 주시면, 담당자가 확인 후
              <br />
              빠르게 연락 드리겠습니다.
            </>
          }
          colorMode="light"
        />

        {submitted ? (
          <div className="mt-24 md:mt-40 max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-[#347E82] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 md:w-10 md:h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3
              className="text-2xl md:text-3xl text-black-almostBlack mb-4"
              style={{ fontFamily: '"pre-Bold", sans-serif' }}
            >
              문의가 접수되었습니다
            </h3>
            <p
              className="text-base md:text-lg text-midGray-50 mb-8"
              style={{ fontFamily: '"pre-Regular", sans-serif' }}
            >
              문의가 정상적으로 전송되었습니다.
              <br />
              빠른 시일 내에 답변 드리겠습니다.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="px-6 py-3 bg-[#347E82] text-white rounded-xl hover:bg-[#2a6568] transition-colors text-sm md:text-base"
              style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
            >
              추가 문의하기
            </button>
          </div>
        ) : (
          <div className="mt-24 md:mt-40">
            <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="company"
                    className={labelClass}
                    style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                  >
                    회사명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    value={form.company}
                    onChange={handleChange}
                    placeholder="회사명을 입력해 주세요"
                    className={inputClass}
                    style={{ fontFamily: '"pre-Regular", sans-serif' }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="name"
                    className={labelClass}
                    style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                  >
                    담당자명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="담당자명을 입력해 주세요"
                    className={inputClass}
                    style={{ fontFamily: '"pre-Regular", sans-serif' }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="phone"
                    className={labelClass}
                    style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                  >
                    연락처 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="010-0000-0000"
                    className={inputClass}
                    style={{ fontFamily: '"pre-Regular", sans-serif' }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className={labelClass}
                    style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                  >
                    이메일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="example@company.com"
                    className={inputClass}
                    style={{ fontFamily: '"pre-Regular", sans-serif' }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="projectType"
                    className={labelClass}
                    style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                  >
                    프로젝트 유형 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    required
                    value={form.projectType}
                    onChange={handleChange}
                    className={inputClass}
                    style={{ fontFamily: '"pre-Regular", sans-serif' }}
                  >
                    <option value="">선택해 주세요</option>
                    {projectTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="budget"
                    className={labelClass}
                    style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                  >
                    예산 규모
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={form.budget}
                    onChange={handleChange}
                    className={inputClass}
                    style={{ fontFamily: '"pre-Regular", sans-serif' }}
                  >
                    <option value="">선택해 주세요</option>
                    {budgetRanges.map((range) => (
                      <option key={range} value={range}>
                        {range}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="content"
                  className={labelClass}
                  style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                >
                  문의 내용 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="content"
                  name="content"
                  required
                  rows={6}
                  value={form.content}
                  onChange={handleChange}
                  placeholder="프로젝트에 대해 자유롭게 설명해 주세요. (목적, 기능, 일정 등)"
                  className={`${inputClass} resize-none`}
                  style={{ fontFamily: '"pre-Regular", sans-serif' }}
                />
              </div>

              {error && (
                <div
                  className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm md:text-base"
                  style={{ fontFamily: '"pre-Regular", sans-serif' }}
                >
                  {error}
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-8 py-4 bg-[#347E82] text-white rounded-xl hover:bg-[#2a6568] transition-colors text-base md:text-lg shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                >
                  {loading ? "전송 중..." : "문의하기"}
                </button>
                <p
                  className="text-sm text-midGray-50"
                  style={{ fontFamily: '"pre-Regular", sans-serif' }}
                >
                  또는 직접 연락:{" "}
                  <a
                    href="mailto:wezon@wezon.co.kr"
                    className="text-[#0075FF] hover:underline"
                  >
                    wezon@wezon.co.kr
                  </a>{" "}
                  /{" "}
                  <a
                    href="tel:042-825-5078"
                    className="text-[#0075FF] hover:underline"
                  >
                    042-825-5078
                  </a>
                </p>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
